// Support log (CR_20260913 S3): each FleetSpoke visit to an account — who, where it was opened from and which roles it
// acted as (CR_20260924), when, why, how it ended — and, per visit, its role changes and what changed (record, action,
// fields; no row data) in its Logs dialog.
// Used by /admin/companies/:id and Settings for managers.
import HistoryIcon from '@mui/icons-material/History';
import LanguageIcon from '@mui/icons-material/Language';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router';
import {
  type SupportChange,
  type SupportRoleChange,
  type SupportSession,
  useSupportChanges,
  useSupportPlaceNames,
  useSupportRecordLabels,
  useSupportSessions,
} from '../data/support';
import { recordPath, recordTarget } from '../lib/supportRecords';
import { dateTime } from '../lib/time';
import { Loading, LoadError } from './Page';

// tables a person edits (the audited ones); anything else shows its technical name
const TABLES = [
  'alert_rules',
  'alerts',
  'api_keys',
  'asset_categories',
  'asset_certificates',
  'asset_condition_reports',
  'asset_financials',
  'asset_request_lines',
  'asset_requests',
  'asset_service_costs',
  'asset_service_periods',
  'asset_service_records',
  'assets',
  'category_cert_rules',
  'category_service_rules',
  'companies',
  'deployments',
  'gateways',
  'inbox',
  'memberships',
  'project_approvers',
  'project_memberships',
  'projects',
  'rate_cards',
  'site_count_owners',
  'site_memberships',
  'site_zones',
  'sites',
  'stock_count_lines',
  'stock_counts',
  'tags',
  'transfer_lines',
  'transfers',
];

// "Transfer line · T-0001": the audited type, then the record it belongs to — a link when this viewer can open it
function RecordCell({
  change,
  labels,
  linkRecords,
}: {
  change: SupportChange;
  labels: Map<string, string | null> | undefined;
  linkRecords: boolean;
}) {
  const { t } = useTranslation();
  const type = TABLES.includes(change.table_name)
    ? t(`support.tbl_${change.table_name}`)
    : change.table_name;
  const target = recordTarget(change);
  const name = target ? labels?.get(`${target.table}:${target.id}`) : undefined;
  // a person without a visible profile is not "deleted"; any other record looked up and missing is
  const deleted = name === null && target?.table !== 'profiles';
  const path = linkRecords && target && !deleted ? recordPath(target) : null;
  const link = (label: string) => (
    <Link component={RouterLink} to={path!}>
      {label}
    </Link>
  );
  return (
    <Stack direction="row" spacing={0.75} alignItems="baseline" flexWrap="wrap" useFlexGap>
      {name ? (
        <>
          <span>{type}</span>
          {path ? (
            link(name)
          ) : (
            <Box component="span" sx={{ fontWeight: 500 }}>
              {name}
            </Box>
          )}
        </>
      ) : path ? (
        link(type)
      ) : (
        <span>{type}</span>
      )}
      {deleted ? (
        <Typography variant="caption" color="text.secondary">
          {t('support.record_deleted')}
        </Typography>
      ) : null}
    </Stack>
  );
}

/** One visit's changes, oldest first — opened from the visit's Logs button. */
function ChangesDialog({
  session,
  timeZone,
  linkRecords,
  onClose,
}: {
  session: SupportSession;
  timeZone: string;
  linkRecords: boolean;
  onClose(): void;
}) {
  const { t, i18n } = useTranslation();
  const changes = useSupportChanges(session.id, true);
  const labels = useSupportRecordLabels(session.id, changes.data);
  const placeNames = useSupportPlaceNames(session.id, session.roles);
  const roleLabel = useRoleLabel();
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        {t('support.col_logs')}
        <Typography variant="body2" color="text.secondary">
          {session.admin_name} · {t(`support.origin_${session.origin}`)} ·{' '}
          {dateTime(session.started_at, i18n.language, timeZone)}
        </Typography>
        {session.reason ? (
          <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
            {session.reason}
          </Typography>
        ) : null}
      </DialogTitle>
      <DialogContent dividers>
        {session.roles.length ? (
          <Stack spacing={0.5} sx={{ mb: 2 }} data-testid="support-role-changes">
            {session.roles.map((r) => (
              <Typography key={r.at} variant="body2">
                <Box component="span" sx={{ color: 'text.secondary', mr: 1 }}>
                  {dateTime(r.at, i18n.language, timeZone)}
                </Box>
                {t('support.role_changed', { role: roleLabel(r) })}
                {reachText(r, placeNames.data, t) ? ` — ${reachText(r, placeNames.data, t)}` : ''}
              </Typography>
            ))}
          </Stack>
        ) : null}
        {changes.isLoading ? (
          <Loading />
        ) : changes.error ? (
          <LoadError error={changes.error} />
        ) : !changes.data?.length ? (
          <Typography color="text.secondary">{t('support.no_changes')}</Typography>
        ) : (
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('support.col_time')}</TableCell>
                  <TableCell>{t('support.col_record')}</TableCell>
                  <TableCell>{t('support.col_action')}</TableCell>
                  <TableCell>{t('support.col_fields')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {changes.data.map((c) => (
                  <TableRow key={c.id}>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {dateTime(c.at, i18n.language, timeZone)}
                    </TableCell>
                    <TableCell>
                      <RecordCell change={c} labels={labels.data} linkRecords={linkRecords} />
                    </TableCell>
                    <TableCell>{t(`support.op_${c.op}`)}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                        {(c.changed_columns ?? []).map((col) => (
                          <Chip
                            key={col}
                            size="small"
                            label={col}
                            sx={{ fontFamily: 'monospace' }}
                          />
                        ))}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.close')}</Button>
      </DialogActions>
    </Dialog>
  );
}

/** "Manager → Project user": every role the visit acted as, in order (CR_20260924 Part C). */
function useRoleLabel() {
  const { t } = useTranslation();
  return (r: Pick<SupportRoleChange, 'act_as'>) => t(`settings.users.role_${r.act_as}`);
}

/** The places a role change gave: "All", or the chosen places by name. */
function reachText(
  r: SupportRoleChange,
  names: Map<string, string> | undefined,
  t: (key: string) => string,
): string {
  if (r.act_as === 'manager') return '';
  if (r.act_scope === 'all') {
    return t(
      r.act_as === 'warehouse_staff'
        ? 'support.reach_all_warehouses'
        : 'support.reach_all_projects',
    );
  }
  return [...(r.act_project_ids ?? []), ...(r.act_site_ids ?? [])]
    .map((id) => names?.get(id) ?? '…')
    .join(', ');
}

/** Web or phone, with its icon (CR_20260924 decision C: the customer sees where a visit was opened). */
function OriginLabel({ origin }: { origin: SupportSession['origin'] }) {
  const { t } = useTranslation();
  const Icon = origin === 'phone' ? PhoneAndroidIcon : LanguageIcon;
  return (
    <Stack
      direction="row"
      spacing={0.5}
      alignItems="center"
      data-testid={`support-origin-${origin}`}
    >
      <Icon fontSize="small" color="action" aria-hidden />
      <span>{t(`support.origin_${origin}`)}</span>
    </Stack>
  );
}

export function SupportLog({
  companyId,
  timeZone,
  intro,
  linkRecords,
}: {
  companyId: string;
  timeZone: string;
  intro: string;
  /** Link each change to its record — only where the viewer is signed in to this account (not /admin). */
  linkRecords: boolean;
}) {
  const { t, i18n } = useTranslation();
  const sessions = useSupportSessions(companyId);
  const roleLabel = useRoleLabel();
  const [logsOf, setLogsOf] = useState<SupportSession | null>(null);

  return (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        {intro}
      </Typography>
      {sessions.isLoading ? <Loading /> : null}
      {sessions.error ? <LoadError error={sessions.error} /> : null}
      {sessions.data && sessions.data.length === 0 ? (
        <Typography color="text.secondary">{t('support.no_sessions')}</Typography>
      ) : null}
      {sessions.data && sessions.data.length > 0 ? (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('support.col_who')}</TableCell>
                <TableCell>{t('support.col_origin')}</TableCell>
                <TableCell>{t('support.col_acted_as')}</TableCell>
                <TableCell>{t('support.col_started')}</TableCell>
                <TableCell>{t('support.col_ended')}</TableCell>
                <TableCell>{t('support.col_reason')}</TableCell>
                <TableCell align="right">{t('support.col_changes')}</TableCell>
                <TableCell>{t('support.col_logs')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sessions.data.map((s) => {
                const live = !s.ended_at && new Date(s.expires_at).getTime() > Date.now();
                return (
                  <TableRow key={s.id} hover>
                    <TableCell>{s.admin_name}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <OriginLabel origin={s.origin} />
                    </TableCell>
                    <TableCell data-testid="support-acted-as">
                      {(s.roles.length ? s.roles : [{ act_as: 'manager' as const }])
                        .map(roleLabel)
                        .join(' → ')}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {dateTime(s.started_at, i18n.language, timeZone)}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {live ? (
                        <Chip size="small" color="primary" label={t('support.open_now')} />
                      ) : (
                        <>
                          {dateTime(s.ended_at ?? s.expires_at, i18n.language, timeZone)}
                          <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                            {t(`support.end_${s.end_reason ?? 'expired'}`)}
                          </Typography>
                        </>
                      )}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 360 }}>{s.reason}</TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                      {s.changes}
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        aria-label={t('support.show_changes')}
                        onClick={() => setLogsOf(s)}
                      >
                        <HistoryIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      ) : null}
      {logsOf ? (
        <ChangesDialog
          session={logsOf}
          timeZone={timeZone}
          linkRecords={linkRecords}
          onClose={() => setLogsOf(null)}
        />
      ) : null}
    </Stack>
  );
}
